// Knowledge Check — contextual mentor questions after key learning moments.
//
// SHIPPING SHAPE (post-polish): no more auto-mount. The detector is silent —
// it just flags trigger moments as fired. HQ uses `useKnowledgePending()` to
// know whether the Mentor's Cottage should glow, and opens the modal only when
// the kid taps the cottage. The cottage IS the surface for "someone wants to
// talk to you" — mentor advice and knowledge checks share the same chip.
//
// Triggers (each fires once per save, detector dispatches MARK_KC_TRIGGERED):
//   1. compounding          — first savings-jar asset (DEPOSIT_TO_SAVINGS)
//   2. first-asset          — first non-empty assets[]
//   3. loss-recovery        — first inSlump true→false with a real expense
//   4. bracket-transition   — first visionBracket(age) > lastVisionBracket
//
// Timing rule for "is there a pending question right now":
//   • dayOfWeek === 1 AND life.lastHarvestedWeek >= settledWeek
//     (between weeks, Payday cleared)
//   • At most once per page-load session (askedThisSessionRef in HQ scope)

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { visionBracket, type KnowledgeId, type Mindset } from '@biz-kid/engine';
import { useGameState } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip } from '../ui/kit.js';
import { CartoonMiko as Miko } from '../ui/cartoon.js';
import { useLife, useLifeDispatch } from './store.js';
import { useVoice } from '../voice/VoiceContext.js';

type Framework = 'Cashflow' | 'Compounding' | 'Risk' | 'Reputation' | 'Leverage';
type MindsetKey = keyof Mindset;

type Question = {
  id: KnowledgeId;
  framework: Framework;
  context: string;
  mood: 'happy' | 'thinking' | 'cheering';
  question: string;
  options: string[];
  correctIdx: number;
  explanation: string;
  reward: { stat: MindsetKey; amount: number };
};

const QUESTIONS: Record<KnowledgeId, Question> = {
  compounding: {
    id: 'compounding',
    framework: 'Compounding',
    context: 'Your savings just earned a little interest for the first time.',
    mood: 'thinking',
    question: 'If you leave it in there instead of spending it, what happens next year?',
    options: [
      "It stays the same — money doesn't grow on its own.",
      'It earns interest, then earns interest on the interest. The pile grows faster every year.',
      "The bank takes some of it back if you don't spend it.",
    ],
    correctIdx: 1,
    explanation: 'Compounding is interest earning interest. A small amount left to grow for years becomes a big amount — that\'s the snowball Buffett talks about.',
    reward: { stat: 'focus', amount: 5 },
  },
  'first-asset': {
    id: 'first-asset',
    framework: 'Cashflow',
    context: 'You just added your first ASSET to your portfolio.',
    mood: 'happy',
    question: 'What makes something an asset and not a liability?',
    options: [
      'It looks expensive and impresses my friends.',
      'It puts money INTO my pocket every month, without me working.',
      'It cost a lot of money to buy.',
    ],
    correctIdx: 1,
    explanation: 'An asset feeds you. A liability eats you. Same B$10,000 spent: one starts paying you back every month, the other costs you every month. Tell them apart — that\'s the whole game.',
    reward: { stat: 'desire', amount: 5 },
  },
  'loss-recovery': {
    id: 'loss-recovery',
    framework: 'Risk',
    context: 'You just bounced back from a tough loss week.',
    mood: 'cheering',
    question: "What's the most important thing to do after a loss?",
    options: [
      'Avoid risk forever so you never lose again.',
      'Look at the numbers, change one thing, try again.',
      'Blame someone else and move on.',
    ],
    correctIdx: 1,
    explanation: 'Every loss is a lesson with a price tag. Successful founders don\'t avoid losses — they look at exactly what happened, fix one thing, and come back. The number of comebacks beats the number of perfect runs.',
    reward: { stat: 'persistence', amount: 5 },
  },
  'bracket-transition': {
    id: 'bracket-transition',
    framework: 'Reputation',
    context: "You've crossed into a new life stage — a year older, a bigger world.",
    mood: 'thinking',
    question: 'What\'s a "vision"?',
    options: [
      'Something you wish for and wait for.',
      'A picture of what you\'ll build, plus the small steps you\'ll take to get there.',
      'Whatever feels exciting today.',
    ],
    correctIdx: 1,
    explanation: 'A vision is a goal with a path. Wishing alone doesn\'t move anything — but a clear picture plus a tiny daily step does. That\'s why we pin dreams on the Vision Board and then go play.',
    reward: { stat: 'faith', amount: 5 },
  },
};

const TRIGGER_ORDER: KnowledgeId[] = ['compounding', 'first-asset', 'loss-recovery', 'bracket-transition'];

// ─── Detector ───────────────────────────────────────────────────────────────

/** Silent detector — renders null. Runs the trigger-detection useEffects so
 *  trigger flags get set whenever state changes. Mounted globally so detection
 *  doesn't depend on which screen the kid is on. */
export function KnowledgeCheckDetector() {
  const life = useLife();
  const lifeDispatch = useLifeDispatch();
  const inSlump = useGameState((s) => s.inSlump);
  const ledger = useGameState((s) => s.ledger);
  const prevInSlumpRef = useRef(inSlump);
  const lp = life.learningProgress ?? { triggered: {}, answers: [] };

  // (1) first compounding — savings-jar appears.
  useEffect(() => {
    if (lp.triggered.compounding) return;
    if (life.assets.some((a) => a.id === 'savings-jar')) {
      lifeDispatch({ type: 'MARK_KC_TRIGGERED', id: 'compounding' });
    }
  }, [life.assets, lp.triggered.compounding, lifeDispatch]);

  // (2) first asset — any asset present.
  useEffect(() => {
    if (lp.triggered['first-asset']) return;
    if (life.assets.length > 0) {
      lifeDispatch({ type: 'MARK_KC_TRIGGERED', id: 'first-asset' });
    }
  }, [life.assets.length, lp.triggered, lifeDispatch]);

  // (3) loss recovery — inSlump just flipped true → false.
  useEffect(() => {
    const wasInSlump = prevInSlumpRef.current;
    prevInSlumpRef.current = inSlump;
    if (lp.triggered['loss-recovery']) return;
    if (wasInSlump && !inSlump) {
      const hadLoss = ledger.some((t) => t.amount < 0 && t.category === 'expense');
      if (hadLoss) lifeDispatch({ type: 'MARK_KC_TRIGGERED', id: 'loss-recovery' });
    }
  }, [inSlump, ledger, lp.triggered, lifeDispatch]);

  // (4) bracket transition — visionBracket(age) > lastVisionBracket.
  useEffect(() => {
    if (lp.triggered['bracket-transition']) return;
    if (visionBracket(life.age) > life.lastVisionBracket && life.lastVisionBracket > 0) {
      lifeDispatch({ type: 'MARK_KC_TRIGGERED', id: 'bracket-transition' });
    }
  }, [life.age, life.lastVisionBracket, lp.triggered, lifeDispatch]);

  return null;
}

// ─── Hook + Modal — HQ controls the open ────────────────────────────────────

export type PendingKnowledge = {
  question: Question;
  submit: (chosenIdx: number) => void;
};

/** Returns a pending KC if there is one to ask right now, else null.
 *  "Right now" = between weeks (dayOfWeek===1, payday cleared) AND at least
 *  one triggered-but-unanswered question. HQ calls this to know whether to
 *  glow the cottage and what to mount when the cottage is tapped. */
export function useKnowledgePending(): PendingKnowledge | null {
  const life = useLife();
  const lifeDispatch = useLifeDispatch();
  const week = useGameState((s) => s.clock.week);
  const dayOfWeek = useGameState((s) => s.clock.dayOfWeek);

  const lp = life.learningProgress ?? { triggered: {}, answers: [] };
  const settledWeek = week - 1;
  const paydayDone = (life.lastHarvestedWeek ?? 0) >= settledWeek;
  const betweenWeeks = dayOfWeek === 1 && paydayDone;

  return useMemo(() => {
    if (!betweenWeeks) return null;
    const pendingId = TRIGGER_ORDER.find(
      (id) => lp.triggered[id] && !lp.answers.find((a) => a.id === id),
    );
    if (!pendingId) return null;
    const q = QUESTIONS[pendingId];
    return {
      question: q,
      submit: (chosenIdx) => {
        const correct = chosenIdx === q.correctIdx;
        lifeDispatch({
          type: 'RECORD_KC_ANSWER',
          answer: { id: q.id, framework: q.framework, askedAtWeek: week, correct, chosenIdx },
        });
        if (correct) {
          lifeDispatch({ type: 'ADJUST_MINDSET', deltas: { [q.reward.stat]: q.reward.amount } });
        }
      },
    };
  }, [betweenWeeks, lp.triggered, lp.answers, lifeDispatch, week]);
}

/** The modal itself — controlled by the parent. Parent decides when to mount
 *  and when to unmount. Calls submit() once on answer, then onClose on Continue. */
export function KnowledgeCheckModal({ pending, onClose }: { pending: PendingKnowledge; onClose: () => void }) {
  const q = pending.question;
  const [chosen, setChosen] = useState<number | null>(null);
  const correct = chosen !== null && chosen === q.correctIdx;

  // MIKO SPEAKS — read the context + question aloud once per question (young
  // readers play by ear); the on-screen options stay silent so the kid reads
  // and weighs the choices themselves.
  const { speak } = useVoice();
  const spokeIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (spokeIdRef.current === q.id) return;
    spokeIdRef.current = q.id;
    void speak(`Brain check! ${q.context} ${q.question}`);
  }, [q, speak]);

  const pick = (idx: number) => {
    if (chosen !== null) return;
    setChosen(idx);
    pending.submit(idx);
    // Speak the verdict + the clear explanation — the actual teaching moment.
    void speak(`${idx === q.correctIdx ? 'No cap, smart!' : 'Not quite.'} ${q.explanation}`);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 72,
        display: 'grid', placeItems: 'center', padding: 16,
        background: 'color-mix(in srgb, var(--bg) 70%, transparent)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <motion.div
        initial={{ y: 14, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        style={{ width: '100%', maxWidth: 440 }}
      >
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div
              style={{
                width: 52, height: 52, borderRadius: 12, flexShrink: 0,
                background: 'linear-gradient(180deg, color-mix(in srgb, var(--brand) 22%, var(--surface)), var(--surface-2))',
                boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.20)',
                display: 'grid', placeItems: 'center',
              }}
            >
              <Miko size={44} mood={q.mood} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Eyebrow color="var(--brand)">BRAIN CHECK · {q.framework}</Eyebrow>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4, lineHeight: 1.4 }}>{q.context}</div>
            </div>
          </div>

          <p style={{ margin: '12px 0 14px', fontSize: 17, fontWeight: 800, color: 'var(--text)', lineHeight: 1.4 }}>
            {q.question}
          </p>

          <div style={{ display: 'grid', gap: 10 }}>
            {q.options.map((opt, i) => {
              const isChosen = chosen === i;
              const isCorrect = chosen !== null && i === q.correctIdx;
              const isWrongChoice = isChosen && !isCorrect;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pick(i)}
                  disabled={chosen !== null && !isChosen && !isCorrect}
                  style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: 14,
                    border: `2px solid ${isCorrect ? 'var(--up)' : isWrongChoice ? 'var(--down)' : 'var(--border)'}`,
                    background: isCorrect ? 'var(--up-soft)' : isWrongChoice ? 'var(--down-soft)' : 'var(--surface-2)',
                    color: 'var(--text)',
                    fontFamily: 'inherit', fontSize: 14.5, fontWeight: 600,
                    cursor: chosen === null ? 'pointer' : 'default',
                    lineHeight: 1.45,
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}
                >
                  <span style={{ minWidth: 16 }}>
                    {chosen === null ? `${'ABC'[i]}.` : isCorrect ? '✓' : isWrongChoice ? '✗' : `${'ABC'[i]}.`}
                  </span>
                  <span style={{ flex: 1 }}>{opt}</span>
                </button>
              );
            })}
          </div>

          {chosen !== null && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              style={{
                marginTop: 14, padding: 14, borderRadius: 14,
                background: 'var(--surface-2)', border: '1px solid var(--border)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                {correct ? <Chip tone="up">✓ NO CAP, SMART</Chip> : <Chip tone="down">NOT QUITE</Chip>}
                {correct && (
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--up)' }}>
                    +{q.reward.amount} {q.reward.stat}
                  </span>
                )}
              </div>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.55 }}>
                {q.explanation}
              </p>
              <div style={{ marginTop: 12 }}>
                <Button tone="primary" full onClick={onClose}>LET’S GO →</Button>
              </div>
            </motion.div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}

// ─── Parent Dashboard catalog ──────────────────────────────────────────────

export const KNOWLEDGE_CATALOG: Array<{ id: KnowledgeId; label: string; framework: Framework; stat: MindsetKey }> = [
  { id: 'compounding', label: 'Compounding', framework: 'Compounding', stat: 'focus' },
  { id: 'first-asset', label: 'Assets vs liabilities', framework: 'Cashflow', stat: 'desire' },
  { id: 'loss-recovery', label: 'Bouncing back from a loss', framework: 'Risk', stat: 'persistence' },
  { id: 'bracket-transition', label: 'What a "vision" is', framework: 'Reputation', stat: 'faith' },
];
